import * as numeral from 'numeral';

import { environment }       from '../config/environment';
import { fetchJson }             from '../../fetch';
import { LeaderboardRecord } from '../models/LeaderboardRecord';
import { ScoreBoxModel }     from '../models/ScoreBoxModel';
import AuthService           from './AuthService';
import { User }              from '../models/User';
const API_PATH = environment.SCORE_API;

export enum LeaderboardTabs { TODAY = 'day', THIS_WEEK = 'week', THIS_MONTH = 'month' }

type TimeFrames = LeaderboardTabs.TODAY | LeaderboardTabs.THIS_WEEK | LeaderboardTabs.THIS_MONTH;

class RemoteScoreService {

    private currentSlug: string = '';

    private lbStorage: {
        [timeFrame: string]: LeaderboardRecord[]
    } = {};

    public getLeaderboard(slug: string, timeFrame: TimeFrames): Promise<LeaderboardRecord[]> {
        if (timeFrame in this.lbStorage && slug === this.currentSlug) {
            return Promise.resolve(this.lbStorage[timeFrame]);
        }

        this.currentSlug = slug;
        this.lbStorage = {};

        const url = `${API_PATH}/score/leaderboard/${slug}/${timeFrame}?arenaDomain=${environment.ARENA_DOMAIN}`;

        return fetchJson(url)
            .then(data => {
                this.lbStorage[timeFrame] = data.map((item: LeaderboardRecord) => new LeaderboardRecord(item));
                return data;
            });
    }

    public patchLBWithUserScore(slug: string, user: User, score: number = 0) {
        if (score === 0) {
            return;
        }

        return this.getLeaderboard(slug, LeaderboardTabs.TODAY)
            .then(() => {
                const timeFrames = Object.keys(this.lbStorage);

                timeFrames.forEach(timeFrame => {
                    const rows = this.lbStorage[timeFrame];

                    const userExistingIndex = rows.findIndex(row => row.name === user.name);

                    // User already presented in leaderboard
                    if (userExistingIndex !== -1) {
                        // User didn't beat his score
                        if (rows[userExistingIndex].score >= score) {
                            return;
                        }
                        // Otherwise update
                        rows[userExistingIndex].score = score;
                    } else {
                        const userNewRow = new LeaderboardRecord({ name: user.name, score });
                        rows.push(userNewRow);
                    }

                    rows.sort((a, b) => {
                        return b.score - a.score;
                    });
                });
            });
    }

    public async getScoreBox(slug: string): Promise<ScoreBoxModel> {
        return await AuthService.authFetch(`${API_PATH}/aarp/${slug}/scorebox`);
    }

    public async saveScores(scores: Array<{ slug: string, value: number }>) {
        const body = JSON.stringify(scores);
        return await AuthService.authFetch(`${API_PATH}/score`, { method: 'POST', body }, false);
    }

    public formatScore(score: number): string {
        // If score is at least 1,000,000,000 (10 digits), show it as billions
        if (score >= Math.pow(10, 9)) {
          // Still divide by 1e6 to keep the number in the same format (e.g. 1,450,000,000 / 1e6 = 1,450)
          const value = score / Math.pow(10, 6);
          return numeral(value).format('0,00') + ' B';
        } else if (score >= Math.pow(10, 6)) {
          // For scores between 1e6 and 1e9, show in millions
          const value = score / Math.pow(10, 6);
          return numeral(value).format('0,00') + ' M';
        }
        // Otherwise, format the raw score
        return numeral(score).format('0,0');
      }
}

export default new RemoteScoreService();
